import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { MarketingShell } from '../components/MarketingShell';

// Support page at /support. Linked from the App Store listing's required support URL,
// the marketing site, and the app's home screen.
export function SupportScreen() {
  return (
    <MarketingShell>
      <div className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-semibold tracking-tight">Support</h1>
        <p className="text-ink/60 mt-2">We answer every email. Usually within 48 hours.</p>

        <section className="mt-8 bg-white border border-ink/10 rounded-2xl p-6">
          <h2 className="font-semibold text-lg">Email us</h2>
          <p className="mt-2 text-ink/70 leading-relaxed">
            Tell us what is happening. If something is broken, include the make and model of your
            phone, the version of the operating system, and a short description of what you
            tapped before it went wrong. Screenshots help a lot.
          </p>
          <a
            href="mailto:support@mydyslexiatools.com"
            className="inline-flex items-center mt-4 bg-accent text-white px-5 py-2.5 rounded-full font-medium hover:brightness-105 transition"
          >
            support@mydyslexiatools.com
          </a>
        </section>

        <section className="mt-10">
          <h2 className="text-2xl font-semibold tracking-tight">Common questions</h2>
          <div className="mt-4 space-y-4">
            <Faq q="My camera will not open">
              Answer Assist needs permission to use your camera. On iPhone go to Settings →
              Answer Assist → Camera. On Android, go to Settings → Apps → Answer Assist →
              Permissions → Camera. Toggle it on. If you are using the web version, your browser
              will ask the first time you tap "Open Camera". Tap Allow.
            </Faq>

            <Faq q="It rejected my photo and asked me to try again">
              The safeguarding gate refuses photos that do not look like a worksheet. The most
              common reasons are: a person is in the photo, the page is too small in the frame,
              or the lighting is bad. Place the page flat on a table in good light, fill the
              screen with the page, and try again.
            </Faq>

            <Faq q="The dictation does not recognise what my child says">
              Speech recognition needs an internet connection and works best in a quiet room.
              Some browsers do not support speech recognition at all. If yours does not, type
              the answer instead. We are working on better support for younger voices and
              accents.
            </Faq>

            <Faq q="The questions got placed in the wrong order">
              The AI does its best but worksheets vary. If a question is placed in the wrong
              slot, retake the photo with the page held a little straighter and a little
              flatter. If it keeps happening on the same worksheet, email it to us (with school
              names or your child's name blacked out) and we will tune the model.
            </Faq>

            <Faq q="I lost my purchase">
              If you bought the unlock and now do not see it, on iPhone open the App Store, tap
              your profile, then Purchased, and find Answer Assist. On Android, open the
              Play Store app, tap your profile, then Payments and subscriptions. Apple and
              Google handle the receipts. We never see your card details.
            </Faq>

            <Faq q="I want to delete my data">
              We do not collect identifiable data. To delete everything anyway, in the app open
              Settings and tap "Reset device". This clears the anonymous device identifier and
              all local preferences. You can also delete the app from your phone.
            </Faq>
          </div>
        </section>

        <section className="mt-12 bg-warm/50 rounded-2xl p-6">
          <h2 className="text-xl font-semibold tracking-tight">If you or your child are in crisis</h2>
          <p className="mt-2 text-ink/70 leading-relaxed">
            Answer Assist is not a crisis service. If you are worried about a child's safety
            or your own, contact one of these services right away:
          </p>
          <ul className="mt-3 space-y-1 text-ink/80">
            <li>Samaritans: 116 123 (free, 24/7)</li>
            <li>Childline: 0800 1111 (free, for children and young people)</li>
            <li>NSPCC helpline (adults worried about a child): 0808 800 5000</li>
            <li>Emergency: 999</li>
          </ul>
        </section>

        <div className="mt-12 text-center">
          <Link to="/" className="text-accent hover:underline">
            ← Back to mydyslexiatools.com
          </Link>
        </div>
      </div>
    </MarketingShell>
  );
}

function Faq({ q, children }: { q: string; children: ReactNode }) {
  return (
    <details className="bg-white rounded-2xl border border-ink/10 p-5 group">
      <summary className="cursor-pointer flex items-center justify-between font-medium text-ink">
        <span>{q}</span>
        <span className="text-ink/40 group-open:rotate-180 transition">▾</span>
      </summary>
      <div className="mt-3 text-sm text-ink/70 leading-relaxed">{children}</div>
    </details>
  );
}
